import { getUserData } from "../../../global/hooks/getUserRole";
import { useState, useEffect } from "react";
import { Form, Col, Row, Button } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import CustomSwitch from "../../../global/components/inputWidgets/CustomSwitch";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import AccountIcon from "../../../assets/images/vectors/SideBarIcons/AccountIcon";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";
import DealerIcon from "../../../assets/images/vectors/SideBarIcons/DealerIcon";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { normalizeEmailValue } from "../../../global/utils/normalizeFormEmails";

/** API / Select may use string values; role checks use numeric .includes() */
const coerceRoleId = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
};

const isEmailValue = (value) => {
  if (typeof value !== "string") return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

/** Null hierarchy FKs in the payload when that field is not shown for this editor + account type (matches form JSX). */
const getHierarchyClearsForSubmit = (accountTypeSubmitted, editorUserRole) => {
  const at = coerceRoleId(accountTypeSubmitted);
  const ur = coerceRoleId(editorUserRole);
  const patch = {};
  if (at == null || ur == null) return patch;

  const showOemField = ![1, 2, 3].includes(at) && [1, 2].includes(ur);
  const showDealerField =
    [7, 8, 9, 10, 11, 12].includes(at) && [1, 2, 4, 5].includes(ur);
  const showCustomerField =
    [10, 11, 12].includes(at) && [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(ur);

  if ([1, 2].includes(ur)) {
    if (!showOemField) {
      patch.OemNo = null;
      patch.DealerNo = null;
      patch.CustomerNo = null;
      return patch;
    }
    if (!showDealerField) {
      patch.DealerNo = null;
      patch.CustomerNo = null;
    } else if (!showCustomerField) {
      patch.CustomerNo = null;
    }
    return patch;
  }

  if ([1, 2, 4, 5].includes(ur)) {
    if (!showDealerField) {
      patch.DealerNo = null;
      patch.CustomerNo = null;
    } else if (!showCustomerField) {
      patch.CustomerNo = null;
    }
    return patch;
  }

  if ([1, 2, 3, 4, 5, 6, 7, 8, 9].includes(ur)) {
    if (!showCustomerField) {
      patch.CustomerNo = null;
    }
  }

  return patch;
};

export const AccountForm = ({
  type,
  onCancel,
  isModalForm,
  setDefaultData: setAccountDefaultData,
  defaultData: accountDefaultData,
  disbaleDealerField,
  disableCustomerField,
}) => {
  const loggedInUser = getUserData();
  const [selectedOemNo, setSelectedOemNo] = useState(loggedInUser?.oem_no);
  const [selectedDealerNo, setSelectedDealerNo] = useState(
    loggedInUser?.dealer_no,
  );
  const [showModal, setShowModal] = useState(false);
  const [accountType, setAccountType] = useState(loggedInUser?.role_value);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [defaultData, setDefaultData] = useState(
    accountDefaultData
      ? accountDefaultData
      : {
          DealerNo: null,
          OemNo: null,
          CustomerNo: null,
        },
  );
  // console.log(defaultData);
  const [form] = Form.useForm();
  const { id: urlId } = useParams();
  const id = defaultData?.AccountNo ? defaultData?.AccountNo : urlId;
  const navigate = useNavigate();
  const location = useLocation();

  const url = isModalForm
    ? defaultData?.AccountNo
      ? `${process.env.REACT_APP_API_URL}/api/accounts/${id}/`
      : null
    : id
      ? `${process.env.REACT_APP_API_URL}/api/accounts/${id}/`
      : `${process.env.REACT_APP_API_URL}/api/accounts/`;
  const modalPostUrl = `${process.env.REACT_APP_API_URL}/api/accounts/`;
  // const dealerOptionsUrl =
  //   selectedOemNo &&
  //   `${process.env.REACT_APP_API_URL}/api/dealers/getDealersByOem/${selectedOemNo}/`;
  // oems/<>/dealers/
  const dealerOptionsUrl =
    selectedOemNo || defaultData?.OemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${
          defaultData?.OemNo ? defaultData.OemNo : selectedOemNo
        }/dealers/`
      : null;

  // const postUrl = `${process.env.REACT_APP_API_URL}/api/accounts/`;
  // const putUrl = `${process.env.REACT_APP_API_URL}/api/accounts/${id}/`;
  // const deleteUrl = `${process.env.REACT_APP_API_URL}/api/accounts/${id}/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const formDealerNo = Form.useWatch("DealerNo", form);
  const formAccountType = Form.useWatch("AccountType", form);
  const isDealerRole = [7, 8, 9].includes(
    coerceRoleId(loggedInUser?.role_value),
  );
  const isOemRole = [4, 5, 6].includes(coerceRoleId(loggedInUser?.role_value));
  const accountTypeId = coerceRoleId(formAccountType ?? accountType);
  const userRoleId = coerceRoleId(loggedInUser?.role_value);
  const dealerNo = isDealerRole ? loggedInUser?.dealer_no : formDealerNo;
  const getCustomersUrl =
    dealerNo &&
    `${process.env.REACT_APP_API_URL}/api/dealers/${dealerNo}/customers/`;
  const {
    data: optionsData,
    loading: optionDataLoading,
    error: optionDataError,
    refetch: optionDataRefetch,
  } = useGet(optionUrl);
  const { data: customerData, refetch: customerDataRefetch } =
    useGet(getCustomersUrl);
  function filterRolesByPathname(roles, pathname) {
    if (pathname.includes("oem")) {
      return roles.filter((role) => [4, 5, 6].includes(role.value));
    } else if (pathname.includes("dealer")) {
      return roles.filter((role) => [7, 8, 9].includes(role.value));
    } else if (pathname.includes("customer")) {
      return roles.filter((role) => [10, 11, 12].includes(role.value));
    }
    // default: return all or system roles
    return roles.filter((role) => [2, 3].includes(role.value));
  }
  const modifiedType = getModifiedResponse(optionsData, "roles");
  const filteredRoles = isModalForm
    ? filterRolesByPathname(modifiedType, location.pathname)
    : modifiedType;

  // const modifiedDealer = getModifiedResponse(optionsData, "dealers");
  // const modifiedCustomer = getModifiedResponse(optionsData, "customers");
  const modifiedOems = getModifiedResponse(optionsData, "oems");
  const { data, loading, error, refetch } = useGet(url);
  const {
    data: dealerOp,
    loading: delaerOpLoading,
    error: dealerOpError,
    refetch: delaerOpRefetch,
  } = useGet(dealerOptionsUrl);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(url);
  const {
    data: postDataModal,
    loading: postLoadingModal,
    error: postErrorModal,
    postData: postActionModal,
  } = usePost(modalPostUrl);

  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(url);
  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
  } = useDelete(url);

  const onFinish = (values) => {
    try {
      const emailValues = { ...values };
      // Blank password on edit means "keep the current password" — never
      // send an empty value to the API.
      if (!String(emailValues.password ?? "").trim()) {
        delete emailValues.password;
      }
      if (!type) {
        if (isEmailValue(values.UserId)) {
          emailValues.Email = normalizeEmailValue(values.UserId);
        } else {
          delete emailValues.Email;
        }
      } else {
        emailValues.Email = normalizeEmailValue(values.Email);
      }
      const hierarchyPatch = getHierarchyClearsForSubmit(
        emailValues?.AccountType,
        loggedInUser?.role_value,
      );
      const normalizedValues = {
        ...emailValues,
        TwoFactor: emailValues?.TwoFactor ? 1 : 0,
        ...hierarchyPatch,
      };
      if (type === "edit") {
        if (
          loggedInUser?.role_value === 10 ||
          loggedInUser?.role_value === 11
        ) {
          const modifiedValues = {
            ...normalizedValues,
            OemNo: loggedInUser.oem_no,
            DealerNo: loggedInUser.dealer_no,
            CustomerNo: loggedInUser.customer_no,
          };
          putAction(modifiedValues);
        } else if (
          loggedInUser?.role_value === 7 ||
          loggedInUser?.role_value === 8
        ) {
          const modifiedValues = {
            ...normalizedValues,
            OemNo: loggedInUser.oem_no,
            DealerNo: loggedInUser.dealer_no,
          };
          putAction(modifiedValues);
        } else if (
          loggedInUser?.role_value === 4 ||
          loggedInUser?.role_value === 5
        ) {
          const modifiedValues = {
            ...normalizedValues,
            OemNo: loggedInUser.oem_no,
          };
          putAction(modifiedValues);
        } else {
          putAction(normalizedValues);
        }
      } else {
        if (isModalForm) {
          const modalHierarchyPatch = getHierarchyClearsForSubmit(
            emailValues?.AccountType,
            loggedInUser?.role_value,
          );
          const modifiedValues = ![4, 5, 6, 7, 8, 9].includes(
            loggedInUser?.role_value,
          )
            ? {
                ...emailValues,
                ...modalHierarchyPatch,
              }
            : {
                ...emailValues,
                ...modalHierarchyPatch,
                DealerNo: defaultData?.DealerNo,
                OemNo: defaultData?.OemNo,
              };

          return postActionModal(modifiedValues);
        }
        if (
          loggedInUser?.role_value === 10 ||
          loggedInUser?.role_value === 11
        ) {
          const modifiedValues = {
            ...normalizedValues,
            OemNo: loggedInUser.oem_no,
            DealerNo: loggedInUser.dealer_no,
            CustomerNo: loggedInUser.customer_no,
          };
          postAction(modifiedValues);
        } else if (
          loggedInUser?.role_value === 7 ||
          loggedInUser?.role_value === 8
        ) {
          const modifiedValues = {
            ...normalizedValues,
            OemNo: loggedInUser.oem_no,
            DealerNo: loggedInUser.dealer_no,
          };
          postAction(modifiedValues);
        } else if (
          loggedInUser?.role_value === 4 ||
          loggedInUser?.role_value === 5
        ) {
          const modifiedValues = {
            ...normalizedValues,
            OemNo: loggedInUser?.oem_no,
          };
          postAction(modifiedValues);
        } else {
          postAction(normalizedValues);
        }
      }
    } catch (error) {
      console.log(error);
      openErrorNotification("Something went wrong");
    }
  };

  const onReset = () => {
    isModalForm && form.resetFields();
    onCancel ? onCancel() : navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Failed!");
    }
    setShowModal(false);
  };

  useEffect(() => {
    delaerOpRefetch();
  }, [selectedOemNo]);

  useEffect(() => {
    if (isModalForm) return;
    const at = coerceRoleId(formAccountType ?? accountType);
    if (at == null) return;
    const ur = coerceRoleId(loggedInUser?.role_value);
    const showOemField = ![1, 2, 3].includes(at) && [1, 2].includes(ur);
    const showDealerField =
      [7, 8, 9, 10, 11, 12].includes(at) && [1, 2, 4, 5].includes(ur);
    const showCustomerField =
      [10, 11, 12].includes(at) && [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(ur);

    const formPatch = {};
    const defaultPatch = {};
    if (!showOemField) {
      formPatch.OemNo = "";
      defaultPatch.OemNo = "";
      if (isOemRole) {
        setSelectedOemNo(loggedInUser?.oem_no);
      } else {
        setSelectedOemNo("");
      }
    }
    if (!showDealerField) {
      formPatch.DealerNo = "";
      defaultPatch.DealerNo = "";
      if (isDealerRole) {
        setSelectedDealerNo(loggedInUser?.dealer_no);
      } else {
        setSelectedDealerNo("");
      }
    }
    if (!showCustomerField) {
      formPatch.CustomerNo = "";
      defaultPatch.CustomerNo = "";
    }
    if (Object.keys(formPatch).length > 0) {
      form.setFieldsValue(formPatch);
      setDefaultData((prev) => ({ ...prev, ...defaultPatch }));
    }
  }, [
    accountType,
    formAccountType,
    isModalForm,
    loggedInUser?.role_value,
    form,
  ]);
  // console.log(accountDefaultData, "accountDefaultData");

  // useEffect(() => {
  //   if (!id && !isModalForm) {
  //     form.setFieldsValue({
  //       AccountType: filteredRoles[0]?.value,
  //     });
  //   }
  //   if (isModalForm && !defaultData?.AccountNo) {
  //     form.setFieldsValue({
  //       AccountType: filteredRoles[0]?.value,
  //     });
  //     setAccountType(filteredRoles[0]?.value);
  //   }
  // }, [optionsData]);

  // console.log("defaultData?.OemNo", data);

  useEffect(() => {
    if (!id || loading) return;
    const getAccountInfoById = () => {
      form.resetFields();
      try {
        if (data?.data) {
          form.setFieldsValue({
            UserId: data?.data?.UserId ? String(data?.data?.UserId) : "",
            AccountName: data.data.AccountName
              ? String(data.data.AccountName)
              : "",
            AccountDesc: data.data.AccountDesc
              ? String(data.data.AccountDesc)
              : "",
            Email: data.data.Email ? String(data.data.Email) : "",
            AccountType: data.data.AccountType ? data.data.AccountType : "",
            OemNo: data.data.OemNo ? Number(data.data.OemNo) : "",
            DealerNo: data.data.DealerNo ? data.data.DealerNo : "",
            CustomerNo: data.data.CustomerNo ? data.data.CustomerNo : "",
            TwoFactor: data.data.TwoFactor ? 1 : 0,
          });
          setAccountType(data.data.AccountType);
          setSelectedOemNo(data.data.OemNo);
          setSelectedDealerNo(data.data.DealerNo);
          setDefaultData((prev) => ({
            ...prev,
            OemNo: data.data.OemNo,
            DealerNo: data.data.DealerNo,
            CustomerNo: data.data.CustomerNo,
          }));
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getAccountInfoById();
  }, [id, data, loading, form, type]);

  //toat for create
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");

      setTimeout(() => {
        navigate(-1);
      }, 400);
    }
  }, [postError, postData, postLoading]);
  //toat for create
  useEffect(() => {
    if (postErrorModal) {
      openErrorNotification(
        postErrorModal?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoadingModal && !postErrorModal && postDataModal) {
      openConfirmNotification("Success");

      setTimeout(() => {
        onCancel();
      }, 400);
    }
  }, [postErrorModal, postDataModal, postLoadingModal]);

  //toat for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");

      refetch();
      setTimeout(() => {
        if (defaultData?.AccountNo) {
          onCancel();
        } else {
          navigate("/account");
        }
      }, 100);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      // refetch();
      navigate("/account");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "Account",
      href: "/account",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/account/info/${id}`
          : `/account/edit/${id}`
        : "/account/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/account/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  const handleCreateNew = (type) => {
    setModalType(type);
    setIsOpenModal(true);
  };
  useEffect(() => {
    optionDataRefetch();
    delaerOpRefetch();
    customerDataRefetch();
    if (defaultData) {
      form.setFieldsValue(defaultData);
    }
  }, [defaultData]);

  return (
    <div>
      <AccountCreationModal
        isOpenModal={isOpenModal}
        modalType={modalType}
        setIsOpenModal={setIsOpenModal}
        setDefaultData={setDefaultData}
        defaultData={defaultData}
        setModalType={setModalType}
        isModal={true}
        isModalForm={true}
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            {modalType === "OEM" ? (
              <OemIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "Dealer" ? (
              <DealerIcon className="text-[15px] text-black ml-1" />
            ) : modalType === "Customer" ? (
              <AccountIcon className="text-[15px] text-black ml-1" />
            ) : null}
            <span className="text-[14px] font-[Poppins] text-black  ml-2">
              {isOpenModal ? `Add ${modalType}` : "Send Invitation"}
            </span>
          </div>
        }
      />

      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          {!isModalForm && (
            <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
              <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
                <CommonButton buttonsData={buttonsData} />
              </CommonBreadcrumb>
            </div>
          )}
          {
            <div
              className={`bg-white overflow-hidden ${
                isModalForm ? "" : "border rounded-[4px]"
              }`}
            >
              {!isModalForm && (
                <div className="flex items-center bg-gray-200 p-2.5">
                  <AccountIcon className="text-[18px] text-black " />
                  <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                    Account
                  </span>
                </div>
              )}
              <div className="px-2 pt-1 pb-3">
                <Row gutter={[16, 8]}>
                  <Col
                    xs={!isModalForm && 24}
                    sm={!isModalForm && 24}
                    md={!isModalForm && 12}
                    lg={!isModalForm && 12}
                    xl={!isModalForm && 12}
                    span={24}
                  >
                    <CustomInput
                      label={!type ? "Username (Email)" : "Username"}
                      name="UserId"
                      disabled={type === "info"}
                      autoComplete="off"
                      required
                      loading={loading}
                    />
                  </Col>
                  {type ? (
                    <Col
                      xs={!isModalForm && 24}
                      sm={!isModalForm && 24}
                      md={!isModalForm && 12}
                      lg={!isModalForm && 12}
                      xl={!isModalForm && 12}
                      span={24}
                    >
                      <CustomInput
                        label="Email"
                        name="Email"
                        type="email"
                        isEmail={true}
                        disabled={type === "info"}
                        required
                        loading={loading}
                      />
                    </Col>
                  ) : null}
                  {type !== "info" && (
                    <Col
                      xs={!isModalForm && 24}
                      sm={!isModalForm && 24}
                      md={!isModalForm && 12}
                      lg={!isModalForm && 12}
                      xl={!isModalForm && 12}
                      span={24}
                    >
                      <CustomInput
                        label="Password"
                        name="password"
                        type="password"
                        disabled={type === "info"}
                        autoComplete="new-password"
                        required={!id}
                        loading={loading}
                      />
                    </Col>
                  )}
                  {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <CustomInput
                        label="Name"
                        name="AccountName"
                        disabled={type === "info"}
                        required
                      />
                    </Col> */}
                  <Col
                    xs={!isModalForm && 24}
                    sm={!isModalForm && 24}
                    md={!isModalForm && 12}
                    lg={!isModalForm && 12}
                    xl={!isModalForm && 12}
                    span={24}
                  >
                    <CustomInput
                      label="Description"
                      name="AccountDesc"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                  <Col
                    xs={!isModalForm && 24}
                    sm={!isModalForm && 24}
                    md={!isModalForm && 12}
                    lg={!isModalForm && 12}
                    xl={!isModalForm && 12}
                    span={24}
                  >
                    <CustomSelect
                      label="Account Type"
                      name="AccountType"
                      options={filteredRoles}
                      placeholder="Please select"
                      onChange={(value) => {
                        setAccountType(value);
                      }}
                      disabled={type === "info"}
                      required
                      loading={loading}
                    />
                  </Col>
                  {![1, 2, 3].includes(accountTypeId) &&
                    [1, 2].includes(userRoleId) && (
                      <Col
                        xs={!isModalForm && 24}
                        sm={!isModalForm && 24}
                        md={!isModalForm && 12}
                        lg={!isModalForm && 12}
                        xl={!isModalForm && 12}
                        span={24}
                      >
                        {/* <CustomSelect
                            label="OEM"
                            name="OemNo"
                            options={modifiedOems}
                            placeholder="Please select"
                            onChange={(value) => {
                              setSelectedOemNo(value);
                              form.setFieldsValue({
                                DealerNo: "",
                              });
                            }}
                            disabled={type === "info"}
                            required
                            handleCreateNew={handleCreateNew}
                            type={"OEM"}
                          /> */}
                        <CustomSelect
                          label="OEM"
                          name="OemNo"
                          options={modifiedOems}
                          disabled={type === "info" || isModalForm}
                          placeholder="Please select"
                          onChange={(value) => {
                            setDefaultData((prev) => ({
                              ...prev,
                              OemNo: value,
                              DealerNo: "",
                              CustomerNo: "",
                            }));
                            setSelectedOemNo(value);
                            form.setFieldsValue({
                              DealerNo: "",
                              CustomerNo: "",
                            });
                          }}
                          required
                          handleCreateNew={handleCreateNew}
                          type={"OEM"}
                          loading={loading}
                        />
                      </Col>
                    )}
                  {[7, 8, 9, 10, 11, 12].includes(accountTypeId) &&
                  [1, 2, 4, 5].includes(userRoleId) ? (
                    <Col
                      xs={!isModalForm && 24}
                      sm={!isModalForm && 24}
                      md={!isModalForm && 12}
                      lg={!isModalForm && 12}
                      xl={!isModalForm && 12}
                      span={24}
                    >
                      {/* <CustomSelect
                            label="Dealer"
                            name="DealerNo"
                            options={
                              dealerOp?.data?.map((item) => ({
                                label: item.name,
                                value: item.value,
                              })) || []
                            }
                            placeholder="Please select"
                            disabled={type === "info"}
                            required
                            handleCreateNew={handleCreateNew}
                            type={"Dealer"}
                          /> */}
                      <CustomSelect
                        label="Dealer"
                        name="DealerNo"
                        disabled={
                          type === "info"
                            ? type === "info"
                            : isModalForm
                              ? isModalForm
                              : disbaleDealerField
                                ? disbaleDealerField
                                : defaultData?.OemNo
                                  ? !defaultData?.OemNo
                                  : !selectedOemNo
                        }
                        onChange={(value) => {
                          setDefaultData((prev) => ({
                            ...prev,
                            DealerNo: value,
                            CustomerNo: "",
                          }));
                          setSelectedDealerNo(value);
                          form.setFieldsValue({
                            CustomerNo: "",
                          });
                        }}
                        options={
                          dealerOp?.data?.map((item) => ({
                            label: item.name,
                            value: item.value,
                          })) || []
                        }
                        placeholder="Please select"
                        required
                        handleCreateNew={handleCreateNew}
                        type={"Dealer"}
                        loading={loading}
                      />
                    </Col>
                  ) : null}
                  {[10, 11, 12].includes(accountTypeId) &&
                    [1, 2, 3, 4, 5, 6, 7, 8, 9].includes(userRoleId) && (
                      <Col
                        xs={!isModalForm && 24}
                        sm={!isModalForm && 24}
                        md={!isModalForm && 12}
                        lg={!isModalForm && 12}
                        xl={!isModalForm && 12}
                        span={12}
                      >
                        {/* <CustomSelect
                            label="Customer"
                            name="CustomerNo"
                            options={
                              customerData?.data?.map((item) => ({
                                label: item.name,
                                value: item.value,
                              })) || []
                            }
                            placeholder="Please select"
                            disabled={type === "info"}
                            required
                            handleCreateNew={handleCreateNew}
                            type={"Customer"}
                          /> */}
                        {/* {console.log("defaultData?.DealerNo", defaultData)} */}
                        <CustomSelect
                          disabled={
                            // defaultData?.OemNo && defaultData?.DealerNo
                            //   ? !defaultData?.OemNo || !defaultData?.DealerNo
                            //   : !selectedOemNo ||
                            //     !selectedDealerNo ||
                            type === "info"
                              ? type === "info"
                              : isModalForm
                                ? isModalForm
                                : disableCustomerField
                                  ? disableCustomerField
                                  : defaultData?.DealerNo
                                    ? !defaultData?.DealerNo
                                    : !selectedDealerNo
                          }
                          label="Customer"
                          name="CustomerNo"
                          options={
                            customerData?.data?.map((item) => ({
                              label: item.name,
                              value: item.value,
                            })) || []
                          }
                          placeholder="Please select"
                          required
                          handleCreateNew={handleCreateNew}
                          type={"Customer"}
                          loading={loading}
                        />
                      </Col>
                    )}
                  <Col
                    xs={!isModalForm && 24}
                    sm={!isModalForm && 24}
                    md={!isModalForm && 12}
                    lg={!isModalForm && 12}
                    xl={!isModalForm && 12}
                    span={12}
                  >
                    <CustomSwitch
                      label="Two Factor Authentication"
                      name="TwoFactor"
                      defaultChecked={0}
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          }
        </div>
      </Form>

      {isModalForm && (
        <div className="flex justify-center space-x-2 mt-2.5">
          <Button
            type="primary"
            className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center "
            onClick={() => form.submit()}
            loading={postLoadingModal || putLoading}
          >
            <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
            <span className="">Apply</span>
          </Button>
          <Button className="reset-button" onClick={onReset}>
            <CloseOutlined />
            <span className="">Cancel</span>
          </Button>
        </div>
      )}
      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </div>
  );
};
